import React, { useEffect, useState, useMemo, useCallback, useRef, useContext } from 'react';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { View, Text, Title, StyledModal, StyledModalHandle } from '@constants/Themed';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import KnowledgeItem from './Components/KnowledgeItem';
import { useKnowledgeQuery, createPaginationState, updatePaginationState, resetPaginationPage } from '@utils/knowledgeQueries';
import { Pagination } from '@components/Global/Pagination/Pagination';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useDispatch, useSelector } from 'react-redux';
import { Platform, Pressable, FlatList, SectionList, useWindowDimensions, TextInput, Image, Alert } from 'react-native';
import { useTranslation } from 'react-i18next';
import Button from '@components/UI/Button/Button';
import toTitleCase from '@hooks/toTitleCase';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import BottomSheet from '@gorhom/bottom-sheet';
import type { TabsContentProps } from 'tamagui'
import DeleteConfirmModal from '@components/modals/DeleteConfirmModal/DeleteConfirmModal';
import { Card, H2, H5, Label, Separator, SizableText, Tabs, XStack, YStack } from 'tamagui';
import { LineGraph } from 'react-native-graph';
import { useDeviceType } from '@hooks/useDeviceType';
import { SelectionDot } from '@components/CustomSelectionDot';
import { GraphPoint, GraphRange } from 'react-native-graph/lib/typescript/LineGraphProps';
import gaussian from 'gaussian';
import { AntDesign, Feather, MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import ErrorBoundary from '@utils/ErrorBoundary';
import { uniqueId } from 'lodash';
import GlobalTutorial from '@components/TutorialComponent/GlobalTutorial';
import { knowledgeBaseCarouselData } from '../ProjectManagementScreen/Tutorial/CarouselData';
import styles from '@stylesheet';
import { Dialog } from 'tamagui';
import RowComponent from '@components/Global/RowComponent/RowComponent';
import SwitchWithIcons from 'react-native-switch-with-icons';
import { MotiView } from 'moti';
import DomEditor from '@navigation/screens/LowLevel/Components/DomEditor';
import { MenuItem, Select } from '@mui/material';
import { ToolbarContext, EditorContextType } from '@hooks/toolbarContext';
import GroupsFlatList, { KnowledgeGroup } from './Components/GroupsFlatList';


function weightedRandom(mean: number, variance: number): number {
  var distribution = gaussian(mean, variance)
  // Take a random sample using inverse transform sampling method.
  return distribution.ppf(Math.random())
}

export function generateRandomGraphData(length: number): GraphPoint[] {
  return Array<number>(length)
    .fill(0)
    .map((_, index) => ({
      date: new Date(
        new Date(2000, 0, 1).getTime() + 1000 * 60 * 60 * 24 * index
      ),
      value: weightedRandom(100, Math.pow(index + 1, 3)),
    }))
}

const KNOWLEDGE_ITEM_WIDTH = 350; // Reduced from 500 for better grid layout
const POINT_COUNT = 40
const POINTS = generateRandomGraphData(POINT_COUNT)

const KnowledgeScreen = ({ navigation }) => {
  const theme = themeSelector();
  const [selectedItem, setSelectedItem] = useState();
  const selectedId = useSelector((state: DefaultRootState) => state.app.selectedProfile);

  const { t } = useTranslation();
  const [addKnowledge, setAddKnowledge] = useState(false);
  const [knowledgeType, setKnowledgeType] = useState('internal');
  const knowldegeRef = useRef<StyledModalHandle>(null);
  const [previewFlipped, setPreviewFlipped] = useState(false);
  const [listWidth, setListWidth] = useState(0);
  const [showChart, setShowChart] = useState(false);
  const snapPoints = useMemo(() => ['20%', Platform.select({ web: '70%', default: '90%' })], []);
  const { isTablet } = useDeviceType();
  const [columns, setColumns] = useState(1);
  const bottomSheetRef = useRef<BottomSheet>(null);
  const { width } = useWindowDimensions();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState(false);
  const [newKnowledge, setNewKnowledge] = useState(false);
  const [newKnowledgeTitle, setNewKnowledgeTitle] = useState('');
  const domEditorRef = useRef(null);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImageUrl, setFeaturedImageUrl] = useState(null);
  const [featuredImageBase64, setFeaturedImageBase64] = useState(null);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [articleUrl, setArticleUrl] = useState('');
  const [scrapingArticle, setScrapingArticle] = useState(false);
  
  // Header collapsible state (internal to this component)
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);

  // Groups state management
  const [groups, setGroups] = useState<KnowledgeGroup[]>(() => {
    // Load from localStorage if available
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('knowledgeGroups');
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        console.error('Failed to load groups from localStorage:', e);
      }
    }
    return [];
  });
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [dragOverGroupId, setDragOverGroupId] = useState<string | null>(null);
  const [showAddToGroupModal, setShowAddToGroupModal] = useState(false);
  const [itemToAddToGroup, setItemToAddToGroup] = useState<string | null>(null);
  const addToGroupModalRef = useRef<StyledModalHandle>(null);

  // Save groups to localStorage whenever they change
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        localStorage.setItem('knowledgeGroups', JSON.stringify(groups));
      } catch (e) {
        console.error('Failed to save groups to localStorage:', e);
      }
    }
  }, [groups]);

  // Get editor context from ToolbarContext
  const { setEditorContext } = useContext(ToolbarContext);
  const { height } = useWindowDimensions();

    const textHelp = useMemo(() => [ // Memoize textHelp
      'Manage your internal articles to keep your team informed and up-to-date.',
      'External articles help you share knowledge with clients and the public.',
      'Easily organize, edit, and publish both internal and external knowledge articles.',
      'Switch between internal and external views to manage content visibility.',
      'Use tags and categories to make articles easy to find and maintain.',
      'Easily publish articles to connected platforms with just a few clicks.',
    ], []);

  // Convert file to base64
  const convertFileToBase64 = useCallback((file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  }, []);

  // Handle featured image URL management
  useEffect(() => {
    if (featuredImage) {
      setImageProcessing(true);
      
      // Clean up previous URL if it exists
      if (featuredImageUrl) {
        URL.revokeObjectURL(featuredImageUrl);
      }
      
      // Create new URL for display
      const newUrl = URL.createObjectURL(featuredImage);
      setFeaturedImageUrl(newUrl);
      
      // Convert to base64 for API submission
      convertFileToBase64(featuredImage)
        .then((base64) => {
          setFeaturedImageBase64(base64);
          setImageProcessing(false);
        })
        .catch((error) => {
          console.error('Failed to convert image to base64:', error);
          setFeaturedImageBase64(null);
          setImageProcessing(false);
        });
    } else {
      // Clean up URL when image is removed
      if (featuredImageUrl) {
        URL.revokeObjectURL(featuredImageUrl);
        setFeaturedImageUrl(null);
      }
      setFeaturedImageBase64(null);
      setImageProcessing(false);
    }

    // Cleanup function to revoke URL on unmount or when image changes
    return () => {
      if (featuredImageUrl) {
        URL.revokeObjectURL(featuredImageUrl);
      }
    };
  }, [featuredImage, convertFileToBase64]);

  // Cleanup image URL when component unmounts
  useEffect(() => {
    return () => {
      if (featuredImageUrl) {
        URL.revokeObjectURL(featuredImageUrl);
      }
    };
  }, []);

  // Set up editor context for HelpSidePanel integration
  useEffect(() => {
    if (newKnowledge && setEditorContext) {
      const knowledgeEditorContext: EditorContextType = {
        ref: domEditorRef,
        insertContent: (content: string) => {
          if (domEditorRef.current) {
            try {
              const currentContent = domEditorRef.current.getContent() || '';
              const separator = currentContent.trim() ? '\n\n' : '';
              const newContent = currentContent + separator + content;
              domEditorRef.current.setContent(newContent);
              console.log('Content inserted into knowledge editor successfully');
            } catch (error) {
              console.error('Failed to insert content into knowledge editor:', error);
            }
          }
        },
        setContent: (content: string) => {
          if (domEditorRef.current) {
            try {
              domEditorRef.current.setContent(content);
              console.log('Content set in knowledge editor successfully');
            } catch (error) {
              console.error('Failed to set content in knowledge editor:', error);
            }
          }
        },
        getContent: () => {
          try {
            return domEditorRef.current?.getContent() || '';
          } catch (error) {
            console.error('Failed to get content from knowledge editor:', error);
            return '';
          }
        },
        appendContent: (content: string) => {
          if (domEditorRef.current) {
            try {
              const currentContent = domEditorRef.current.getContent() || '';
              const newContent = currentContent + content;
              domEditorRef.current.setContent(newContent);
              console.log('Content appended to knowledge editor successfully');
            } catch (error) {
              console.error('Failed to append content to knowledge editor:', error);
            }
          }
        },
        prependContent: (content: string) => {
          if (domEditorRef.current) {
            try {
              const currentContent = domEditorRef.current.getContent() || '';
              const newContent = content + currentContent;
              domEditorRef.current.setContent(newContent);
              console.log('Content prepended to knowledge editor successfully');
            } catch (error) {
              console.error('Failed to prepend content to knowledge editor:', error);
            }
          }
        },
        clearContent: () => {
          if (domEditorRef.current) {
            try {
              domEditorRef.current.setContent('');
              console.log('Knowledge editor content cleared successfully');
            } catch (error) {
              console.error('Failed to clear knowledge editor content:', error);
            }
          }
        },
        isConnected: true,
        contentWidth: width,
        contentHeight: height
      };

      // Register the editor context
      setEditorContext(knowledgeEditorContext);
      console.log('Knowledge editor context registered with HelpSidePanel');
    } else if (!newKnowledge && setEditorContext) {
      // Clear editor context when knowledge editor is closed
      setEditorContext(null);
      console.log('Knowledge editor context cleared from HelpSidePanel');
    }

    return () => {
      // Cleanup on unmount
      if (setEditorContext) {
        setEditorContext(null);
      }
    };
  }, [newKnowledge, setEditorContext, width, height]); // Removed domEditorRef.current from dependencies

  useEffect(() => {
    if (width > KNOWLEDGE_ITEM_WIDTH) {
      calculateColumns();
    }
  }, [width])

  const calculateColumns = async () => {
    // Calculate columns with better responsive behavior
    let calculatedColumns = Math.floor(width / KNOWLEDGE_ITEM_WIDTH);
    
    // Ensure minimum and maximum columns for better UX
    if (calculatedColumns < 1) calculatedColumns = 1;
    if (calculatedColumns > 3) calculatedColumns = 3; // Max 3 columns for readability
    
    setColumns(calculatedColumns);
  }

  // Pagination state using utils
  const [paginationState, setPaginationState] = useState(() =>
    createPaginationState({ currentPage: 1, pageSize: 10 })
  );

  const { status, data: knowledgeData, error, isFetching, isLoading, refetch } = useKnowledgeQuery({
    selectedId: selectedId?.id,
    pagination: paginationState,
    enabled: !!selectedId?.id
  });

  // Fallback query that works without selectedProfile
  const { data: fallbackKnowledgeData, isLoading: fallbackKnowledgeLoading } = useQuery({
    queryKey: ['knowledge-no-profile', paginationState.currentPage, paginationState.pageSize, paginationState.sortBy, paginationState.sortOrder],
    queryFn: async () => {
      const data = await apis.getKnowledgeBase(null, {
        page: paginationState.currentPage,
        limit: paginationState.pageSize,
        sortBy: paginationState.sortBy,
        sortOrder: paginationState.sortOrder
      });
      return data.data;
    },
    enabled: !selectedId?.id, // Only run if no selectedProfile
    retry: 0
  });

  // Fallback to old API if new one fails
  const { data: fallbackData, isLoading: fallbackLoading } = useQuery({
    queryKey: ['knowledge-fallback', selectedId?.id],
    queryFn: async () => {
      const data = await apis.getKnowledgeBase(selectedId?.id);
      return data.data;
    },
    enabled: !!selectedId?.id && (!knowledgeData || (knowledgeData.internal?.length === 0 && knowledgeData.external?.length === 0)),
    retry: 0
  });

  // Use fallback data if main query has no data
  const finalKnowledgeData = knowledgeData || fallbackKnowledgeData || fallbackData;

  // Debug logging
  console.log('KnowledgeScreen Debug:', {
    selectedId: selectedId?.id,
    paginationState,
    status,
    isLoading,
    isFetching,
    error,
    knowledgeData: knowledgeData ? {
      internal: knowledgeData.internal?.length || 0,
      external: knowledgeData.external?.length || 0,
      pagination: knowledgeData.pagination
    } : null,
    fallbackKnowledgeData: fallbackKnowledgeData ? {
      internal: fallbackKnowledgeData.internal?.length || 0,
      external: fallbackKnowledgeData.external?.length || 0,
      pagination: fallbackKnowledgeData.pagination
    } : null,
    fallbackData: fallbackData ? {
      internal: fallbackData.internal?.length || 0,
      external: fallbackData.external?.length || 0,
      pagination: fallbackData.pagination
    } : null,
    finalKnowledgeData: finalKnowledgeData ? {
      internal: finalKnowledgeData.internal?.length || 0,
      external: finalKnowledgeData.external?.length || 0,
      pagination: finalKnowledgeData.pagination
    } : null
  });

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setPaginationState(prev => updatePaginationState(prev, { currentPage: page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPaginationState(prev => resetPaginationPage(updatePaginationState(prev, { pageSize })));
  }, []);

  const handleSortByChange = useCallback((sortBy: string) => {
    setPaginationState(prev => resetPaginationPage(updatePaginationState(prev, { sortBy })));
  }, []);

  const handleSortOrderChange = useCallback((sortOrder: 'asc' | 'desc') => {
    setPaginationState(prev => resetPaginationPage(updatePaginationState(prev, { sortOrder })));
  }, []);

  // Group management functions
  const handleGroupCreate = useCallback((groupName: string, category?: string) => {
    const newGroup: KnowledgeGroup = {
      id: `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: groupName,
      itemIds: [],
      category: category || undefined,
      createdAt: new Date().toISOString(),
    };
    setGroups(prev => [...prev, newGroup]);
  }, []);

  const handleGroupDelete = useCallback((groupId: string) => {
    setGroups(prev => prev.filter(g => g.id !== groupId));
    if (selectedGroupId === groupId) {
      setSelectedGroupId(null);
    }
  }, [selectedGroupId]);

  const handleGroupRename = useCallback((groupId: string, newName: string) => {
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, name: newName } : g));
  }, []);

  const handleGroupPress = useCallback((group: KnowledgeGroup) => {
    setSelectedGroupId(prev => prev === group.id ? null : group.id);
  }, []);

  const handleAddItemToGroup = useCallback((itemId: string, groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    const isAlreadyInGroup = group?.itemIds.includes(itemId);
    
    if (isAlreadyInGroup) {
      Alert.alert(
        'Already in Group',
        `This article is already in "${group?.name}".`,
        [{ text: 'OK' }]
      );
      return;
    }

    setGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        return { ...g, itemIds: [...g.itemIds, itemId] };
      }
      return g;
    }));
    
    // Show success feedback
    const addedGroup = groups.find(g => g.id === groupId);
    Alert.alert(
      'Added to Group',
      `Article has been added to "${addedGroup?.name || 'group'}".`,
      [{ text: 'OK' }]
    );
    
    setItemToAddToGroup(null);
    setShowAddToGroupModal(false);
    
    // Close modal using ref if available
    if (addToGroupModalRef.current) {
      addToGroupModalRef.current.closeModal?.();
    }
  }, [groups]);

  const handleRemoveItemFromGroup = useCallback((itemId: string, groupId: string) => {
    setGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return { ...group, itemIds: group.itemIds.filter(id => id !== itemId) };
      }
      return group;
    }));
  }, []);

  // Drag and drop handlers for web
  const handleDragStart = useCallback((e: any, item: any) => {
    if (Platform.OS !== 'web') return;
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({ itemId: item._id }));
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  }, []);

  const handleDragEnd = useCallback((e: any) => {
    if (Platform.OS !== 'web') return;
    setDraggedItem(null);
    setDragOverGroupId(null);
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  }, []);

  const handleGroupDragOver = useCallback((e: any, groupId: string) => {
    if (Platform.OS !== 'web' || !draggedItem) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverGroupId(groupId);
  }, [draggedItem]);

  const handleGroupDragLeave = useCallback((e: any) => {
    if (Platform.OS !== 'web') return;
    setDragOverGroupId(null);
  }, []);

  const handleGroupDrop = useCallback((e: any, groupId: string) => {
    if (Platform.OS !== 'web' || !draggedItem) return;
    e.preventDefault();
    handleAddItemToGroup(draggedItem._id, groupId);
    setDraggedItem(null);
    setDragOverGroupId(null);
  }, [draggedItem, handleAddItemToGroup]);

  const handleOpenAddToGroupModal = useCallback((itemId: string) => {
    setItemToAddToGroup(itemId);
    setShowAddToGroupModal(true);
    // Open modal using ref if available
    if (addToGroupModalRef.current) {
      addToGroupModalRef.current.openModal?.();
    }
  }, []);

  // Filter knowledge items by selected group
  const filteredKnowledgeData = useMemo(() => {
    if (!finalKnowledgeData) return null;
    if (!selectedGroupId) return finalKnowledgeData;
    
    const selectedGroup = groups.find(g => g.id === selectedGroupId);
    if (!selectedGroup) return finalKnowledgeData;

    const allItems = [...(finalKnowledgeData.internal || []), ...(finalKnowledgeData.external || [])];
    const groupItems = allItems.filter(item => selectedGroup.itemIds.includes(item._id));
    
    return {
      internal: groupItems.filter(item => item.type === 'internal'),
      external: groupItems.filter(item => item.type === 'external'),
      pagination: finalKnowledgeData.pagination,
    };
  }, [finalKnowledgeData, selectedGroupId, groups]);
  const [enableRange, setEnableRange] = useState(true);
  const [points, setPoints] = useState(POINTS)

  const refreshData = useCallback(() => {
    setPoints(generateRandomGraphData(POINT_COUNT))
  }, [])

  const highestDate = useMemo(
    () =>
      points.length !== 0 && points[points.length - 1] != null
        ? points[points.length - 1]!.date
        : undefined,
    [points]
  )

  const range: GraphRange | undefined = useMemo(() => {
    // if range is disabled, default to infinite range (undefined)
    if (!enableRange) return undefined

    if (points.length !== 0 && highestDate != null) {
      return {
        x: {
          min: points[0]!.date,
          max: new Date(highestDate.getTime() + 50 * 1000 * 60 * 60 * 24),
        },
        y: {
          min: -200,
          max: 200,
        },
      }
    } else {
      return {
        y: {
          min: -200,
          max: 200,
        },
      }
    }
  }, [enableRange, highestDate, points])


  const _handleAddKnowledge = (type) => {
    setKnowledgeType(type);
    knowldegeRef.current?.openModal?.();
  }

  const handleScrapeArticle = useCallback(async () => {
    if (!articleUrl || !articleUrl.trim()) {
      Alert.alert('URL Required', 'Please enter a valid article URL');
      return;
    }

    console.log('📚 [FRONTEND] Initiating article scrape for URL:', articleUrl);
    setScrapingArticle(true);
    try {
      const result = await apis.scrapeArticleToKnowledge(articleUrl, undefined, 'internal');
      console.log('✅ [FRONTEND] Article scrape initiated successfully:', result);

      Alert.alert(
        'Article Research Started',
        `The article is being processed. Knowledge ID: ${result.knowledgeId}. It will appear in your knowledge base shortly with "Research" status.`,
        [
          {
            text: 'OK', onPress: () => {
              console.log('🔄 [FRONTEND] Refetching knowledge base...');
              setArticleUrl('');
              setTimeout(() => {
                refetch();
              }, 500);
            }
          }
        ]
      );
    } catch (error) {
      console.error('❌ [FRONTEND] Error scraping article:', error);
      console.error('❌ [FRONTEND] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      Alert.alert(
        'Scraping Failed',
        `Unable to scrape the article. ${error.response?.data?.error || error.message}`,
        [{ text: 'OK' }]
      );
    } finally {
      setScrapingArticle(false);
    }
  }, [articleUrl, refetch]);

  const internalSchema = {
    title: { type: 'string', validation: { required: true }, helpText: "The title of the knowledge article" },
    description: { type: 'string', validation: { required: true }, helpText: "The description of the knowledge article" },
    tags: { ref: 'tags', helpText: "Tag the knowledge article to make it easier to find" },
    category: { type: 'string', validation: { required: true }, helpText: "The category of the knowledge article" },
  }

  const externalSchema = {
    title: { type: 'string', validation: { required: true }, helpText: "The title of the knowledge article" },
    description: { type: 'string', validation: { required: true }, helpText: "The description of the knowledge article" },
    tags: { ref: 'tags', helpText: "Tag the knowledge article to make it easier to find" },
    category: { type: 'string', validation: { required: true }, helpText: "The category of the knowledge article" },
    url: { type: 'string', validation: { required: true }, helpText: "The URL of the knowledge article" },
  }

  const postSchema = useCallback(async (values) => {
    let id: string;
    if (knowledgeType == 'internal') {
      await apis.postInternalKnowledge({ ...values, type: 'internal' }, (progressData) => {
        // Handle progress updates for internal knowledge creation
        console.log('Internal knowledge creation progress:', progressData);
      }).then(res => {
        id = res as string
      })
    } else {
      await apis.postExternalKnowledge({ ...values, type: 'external' }, (progressData) => {
        // Handle progress updates for external knowledge creation
        console.log('External knowledge creation progress:', progressData);
      }).then(res => {
        id = res as string
      })
    }
    if (id) {
      knowldegeRef.current?.closeModal();
      navigation.navigate('Knowledge', { screen: 'EditKnowledge', params: { knowledgeArticleId: id } })
    }
  }, [knowledgeType]);

  const handleChange = (value: boolean) => {
    setFilter(value);
  }

  const _handleFlip = (item: any) => {
    setPreviewFlipped(!previewFlipped);
    if (previewFlipped) {
      setSelectedItem(undefined);
      setPreviewFlipped(false);
    } else {
      setSelectedItem(item);
      setPreviewFlipped(true);
    }
  }

  useEffect(() => {
    console.log('Knowledge type changed:', knowledgeType);
  }, [knowledgeType]);

  // Memoize expensive calculations to prevent unnecessary re-renders
  const headerStats = useMemo(() => {
    const internalCount = finalKnowledgeData?.internal?.length || 0;
    const externalCount = finalKnowledgeData?.external?.length || 0;
    const totalCount = internalCount + externalCount;
    const publishedCount = finalKnowledgeData ?
      [...(finalKnowledgeData.internal || []), ...(finalKnowledgeData.external || [])]
        .filter(item => item.status === 'published').length : 0;
    const draftCount = finalKnowledgeData ?
      [...(finalKnowledgeData.internal || []), ...(finalKnowledgeData.external || [])]
        .filter(item => item.status === 'draft' || !item.status).length : 0;
    const removedCount = finalKnowledgeData ?
      [...(finalKnowledgeData.internal || []), ...(finalKnowledgeData.external || [])]
        .filter(item => item.status === 'removed').length : 0;

    return {
      internalCount,
      externalCount,
      totalCount,
      publishedCount,
      draftCount,
      removedCount
    };
  }, [finalKnowledgeData]);

  // Memoize sorting controls to prevent unnecessary re-renders

  const HeaderComponent = useCallback(() => {
    const { internalCount, externalCount, totalCount, publishedCount, draftCount, removedCount } = headerStats;

    return (
      <Animatable.View
        animation="fadeInDown"
        duration={800}
        style={[
          styles.modernCard,
          theme ? styles.modernBgLight : styles.modernBgDark,
          {
            // marginVertical: 16,
            marginHorizontal: 20,
          }
        ]}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: isHeaderCollapsed ? 0 : 20 }}>
          <View style={{
            backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
            borderRadius: 12,
            padding: 12,
            marginRight: 16
          }}>
            <MaterialIcons
              name="library-books"
              size={28}
              color={theme ? "#0ea5e9" : "#38bdf8"}
            />
          </View>
          <View style={{ flex: 1 }}>
            <H2 style={[
              styles.modernCardTitle,
              {
                color: theme ? "#1e293b" : "#f1f5f9",
                fontSize: 24,
                fontWeight: '700',
                marginBottom: 4
              }
            ]}>
              Knowledge Base
            </H2>
            {!isHeaderCollapsed && (
              <Text style={{
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14,
                top: -20
              }}>
                Manage and organize your knowledge articles
              </Text>
            )}
          </View>
          <Pressable
            onPress={() => setIsHeaderCollapsed(!isHeaderCollapsed)}
            style={{
              padding: 8,
              borderRadius: 8,
              backgroundColor: theme ? "#f1f5f9" : "#1e293b"
            }}
          >
            <MaterialIcons
              name={isHeaderCollapsed ? "expand-more" : "expand-less"}
              size={24}
              color={theme ? "#64748b" : "#94a3b8"}
            />
          </Pressable>
        </View>

        {/* Collapsible Content */}
        {!isHeaderCollapsed && (
          <>
            {/* Stats Grid */}
            <XStack gap={8} flexWrap="wrap" justifyContent="center" alignItems="center">
          {[
            {
              title: 'Total Articles',
              value: totalCount.toString(),
              icon: 'library-books',
              color: theme ? "#0ea5e9" : "#38bdf8",
              bgColor: theme ? "#dbeafe" : "#0c4a6e"
            },
            {
              title: 'Internal',
              value: internalCount.toString(),
              icon: 'business',
              color: theme ? "#059669" : "#10b981",
              bgColor: theme ? "#d1fae5" : "#064e3b"
            },
            {
              title: 'External',
              value: externalCount.toString(),
              icon: 'public',
              color: theme ? "#7c3aed" : "#a78bfa",
              bgColor: theme ? "#ede9fe" : "#312e81"
            },
            {
              title: 'Published',
              value: publishedCount.toString(),
              icon: 'publish',
              color: theme ? "#059669" : "#10b981",
              bgColor: theme ? "#d1fae5" : "#064e3b"
            },
            {
              title: 'Drafts',
              value: draftCount.toString(),
              icon: 'edit',
              color: theme ? "#f59e0b" : "#fbbf24",
              bgColor: theme ? "#fef3c7" : "#451a03"
            }
          ].map((stat, index) => (
            <Animatable.View
              key={index}
              animation="bounceIn"
              delay={index * 200}
              style={[
                styles.modernStatsCard,
                {
                  flexBasis: Platform.OS === 'web' ? '18%' : '30%',
                  flexGrow: 1,
                  minWidth: 120,
                  maxWidth: Platform.OS === 'web' ? 180 : undefined,
                  backgroundColor: stat.bgColor,
                  marginBottom: 8,
                  borderWidth: 1,
                  borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
                }
              ]}
            >
              <MaterialIcons
                name={stat.icon as any}
                size={28}
                color={stat.color}
                style={{ marginBottom: 8 }}
              />
              <Text style={[
                styles.modernStatsNumber,
                {
                  fontSize: 20,
                  fontWeight: '800',
                  color: stat.color,
                  top: -30
                }
              ]}>
                {stat.value}
              </Text>
              <Text style={{
                fontSize: 11,
                fontWeight: '600',
                color: theme ? "#64748b" : "#94a3b8",
                textAlign: 'center'
              }}>
                {stat.title}
              </Text>
            </Animatable.View>
          ))}
        </XStack>

      
          </>
        )}
      </Animatable.View>
    )
  }, [headerStats, theme, _handleAddKnowledge, _handleFlip, setShowChart, showChart, isHeaderCollapsed]);



  return (
    <DefaultScreen
  
      backButton upperLevelScreen scrollDisabled searchBar={{
        extraComponents: (
          <XStack gap={8} alignItems="center" paddingHorizontal={10}>
            <TextInput
              value={articleUrl}
              onChangeText={setArticleUrl}
              placeholder="Enter article URL to scrape..."
              placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
              style={{
                flex: 1,
                minWidth: 250,
                maxWidth: 400,
                padding: 10,
                paddingHorizontal: 14,
                borderRadius: 8,
                backgroundColor: theme ? '#f8fafc' : '#374151',
                color: theme ? '#1f2937' : '#f9fafb',
                fontSize: 14,
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}
            />
            <Pressable
              onPress={handleScrapeArticle}
              disabled={scrapingArticle || !articleUrl.trim()}
              style={{
                backgroundColor: scrapingArticle || !articleUrl.trim() ? '#9ca3af' : '#3b82f6',
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 8,
                opacity: scrapingArticle || !articleUrl.trim() ? 0.6 : 1,
              }}
            >
              {scrapingArticle ? (
                <MaterialIcons name="hourglass-empty" size={20} color="white" />
              ) : (
                <MaterialIcons name="link" size={20} color="white" />
              )}
            </Pressable>
          </XStack>
        ),
      customIcons: [
        {
          functionName: "add",
          icon: <AntDesign name="plus" size={16} color="white" />,
          function: () => {
            setNewKnowledge(true);
          }
        },
        {
          functionName: "refresh",
          icon: <AntDesign name="reload" size={16} color="white" />,
          function: () => {
            refetch();
          }
        },
        // {
        //   functionName: 'showGraph',
        //   icon: <AntDesign name="line-chart" size={16} color="white" />,
        //   function: () => { setShowChart(!showChart) }
        // },
        {
          functionName: 'knowledgeSettings', function: () => {
            setDialogOpen(true);
          }, icon: <Feather name="settings" size={18} color="white" style={{ borderRadius: 50, padding: 7, }} />
        },
        // Example triggers for the new modals (uncomment to use):
        // {
        //   functionName: 'userRetention',
        //   icon: <MaterialIcons name="favorite" size={16} color="white" />,
        //   function: () => setUserRetentionModalOpen(true)
        // },
        // {
        //   functionName: 'aiCredits',
        //   icon: <MaterialIcons name="smart-toy" size={16} color="white" />,
        //   function: () => setAiCreditsModalOpen(true)
        // },
        // {
        //   functionName: 'featureUpgrade',
        //   icon: <MaterialIcons name="trending-up" size={16} color="white" />,
        //   function: () => setFeatureUpgradeModalOpen(true)
        // },
        // {
        //   functionName: 'trialExpiration',
        //   icon: <MaterialIcons name="schedule" size={16} color="white" />,
        //   function: () => setTrialExpirationModalOpen(true)
        // },
        // {
        //   functionName: 'featureLimit',
        //   icon: <MaterialIcons name="block" size={16} color="white" />,
        //   function: () => setFeatureLimitModalOpen(true)
        // },
      ]
    }} style={{ flex: 1 }}>
      <DeleteConfirmModal setState={undefined} deleteFunction={apis.deleteKnowledgeArticle} />
      <Dialog key="calSettings" open={dialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            animation="quick"
            opacity={0.6}
            style={{
              zIndex: 1000,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(8px)',
              })
            }}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            x={0}
            scale={1}
            style={[
              {
                padding: 0,
                minWidth: 500,
                maxWidth: 600,
                maxHeight: "85%",
                borderRadius: 24,
                overflow: 'hidden',
                elevation: 12,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 12 },
                shadowOpacity: 0.15,
                shadowRadius: 32,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
                borderWidth: 1,
                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
              },
              Platform.OS === 'web' && {
                backdropFilter: 'blur(24px)',
                boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
              }
            ]}
            opacity={1}
            y={0}
          >
            {/* Header */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 20,
              borderBottomWidth: 1,
              borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 10,
                }}>
                  <Feather
                    name="settings"
                    size={20}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <YStack flex={1}>
                  <Dialog.Title style={{
                    fontSize: 20,
                    fontWeight: '700',
                    color: theme ? '#1e293b' : '#f1f5f9',
                    marginBottom: 4,
                    margin: 0,
                  }}>
                    Publish Settings
                  </Dialog.Title>
                  <Dialog.Description style={{
                    fontSize: 14,
                    color: theme ? '#64748b' : '#94a3b8',
                    fontWeight: '500',
                    margin: 0,
                  }}>
                    Configure external publishing integrations
                  </Dialog.Description>
                </YStack>
              </XStack>
            </View>

            {/* Content */}
            <View style={{ padding: 24 }}>
              <YStack gap="$4">
                {/* WordPress Integration */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderColor: theme ? '#e5e7eb' : '#4b5563',
                }}>
                  <XStack alignItems="center" justifyContent="space-between">
                    <XStack alignItems="center" gap="$3" flex={1}>
                      <View style={{
                        backgroundColor: theme ? "#fef3c7" : "#451a03",
                        borderRadius: 10,
                        padding: 8,
                      }}>
                        <MaterialIcons
                          name="public"
                          size={20}
                          color={theme ? "#f59e0b" : "#fbbf24"}
                        />
                      </View>
                      <YStack flex={1}>
                        <Text style={{
                          fontSize: 16,
                          fontWeight: '600',
                          color: theme ? '#1e293b' : '#f1f5f9',
                          marginBottom: 2,
                        }}>
                          WordPress
                        </Text>
                        <Text style={{
                          fontSize: 12,
                          color: theme ? '#64748b' : '#94a3b8',
                        }}>
                          {filter ? "Edit mode enabled" : "View mode only"}
                        </Text>
                      </YStack>
                    </XStack>
                    <SwitchWithIcons
                      value={filter}
                      trackColor={{ false: '#cbd5e1', true: '#3b82f6' }}
                      thumbColor={{ false: '#94a3b8', true: '#ffffff' }}
                      animationDuration={200}
                      style={{
                        transform: [{ scale: 0.9 }],
                        elevation: 2,
                        shadowColor: '#000',
                        shadowOffset: { width: 0, height: 2 },
                        shadowOpacity: 0.1,
                        shadowRadius: 4,
                      }}
                      icon={{ false: 'eye', true: 'edit' }}
                      onValueChange={async (value) => {
                        await apis.changePublishSettings('wordpress')
                      }}
                    />
                  </XStack>
                </View>

                {/* Additional Integration Placeholder */}
                <View style={{
                  backgroundColor: theme ? '#f8fafc' : '#374151',
                  borderRadius: 16,
                  padding: 20,
                  borderWidth: 1,
                  borderStyle: 'dashed',
                  borderColor: theme ? '#cbd5e1' : '#6b7280',
                  opacity: 0.6,
                }}>
                  <XStack alignItems="center" justifyContent="center" gap="$3">
                    <AntDesign
                      name="plus"
                      size={24}
                      color={theme ? '#94a3b8' : '#6b7280'}
                    />
                    <Text style={{
                      fontSize: 14,
                      color: theme ? '#94a3b8' : '#6b7280',
                      fontWeight: '500',
                    }}>
                      More integrations coming soon
                    </Text>
                  </XStack>
                </View>
              </YStack>
            </View>

            {/* Footer Actions */}
            <View style={{
              backgroundColor: theme ? '#f8fafc' : '#374151',
              paddingHorizontal: 24,
              paddingVertical: 16,
              borderTopWidth: 1,
              borderTopColor: theme ? '#e5e7eb' : '#4b5563',
            }}>
              <XStack gap="$3" justifyContent="flex-end">
                <Dialog.Close asChild>
                  <Pressable
                    onPress={() => setDialogOpen(false)}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#f1f5f9' : '#4b5563',
                      borderWidth: 1,
                      borderColor: theme ? '#e2e8f0' : '#6b7280',
                      minWidth: 100,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{
                      color: theme ? '#64748b' : '#d1d5db',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Cancel
                    </Text>
                  </Pressable>
                </Dialog.Close>
                <Dialog.Close asChild>
                  <Pressable
                    onPress={() => setDialogOpen(false)}
                    style={{
                      paddingHorizontal: 20,
                      paddingVertical: 12,
                      borderRadius: 12,
                      backgroundColor: theme ? '#3b82f6' : '#2563eb',
                      minWidth: 100,
                      alignItems: 'center',
                      elevation: 2,
                      shadowColor: '#3b82f6',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.2,
                      shadowRadius: 8,
                    }}
                  >
                    <Text style={{
                      color: '#ffffff',
                      fontWeight: '600',
                      fontSize: 14,
                    }}>
                      Save Changes
                    </Text>
                  </Pressable>
                </Dialog.Close>
              </XStack>
            </View>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>

      {/* Add to Group Modal */}
      <StyledModal
        ref={addToGroupModalRef}
        title="Add to Group"
        caption={itemToAddToGroup ? "Select a group to add this knowledge item to" : "Select a group"}
        isVisible={showAddToGroupModal}
        onBackdropPress={() => {
          setShowAddToGroupModal(false);
          setItemToAddToGroup(null);
          if (addToGroupModalRef.current) {
            addToGroupModalRef.current.closeModal?.();
          }
        }}
        style={{
          backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
          borderRadius: 24,
          elevation: 12,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 12 },
          shadowOpacity: 0.15,
          shadowRadius: 32,
          borderWidth: 1,
          borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
          ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(24px)',
            boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
          })
        }}
      >
        <YStack gap="$3" padding="$4">
          {groups.length === 0 ? (
            <View style={{ padding: 20, alignItems: 'center' }}>
              <MaterialIcons
                name="folder-outline"
                size={48}
                color={theme ? '#9ca3af' : '#6b7280'}
                style={{ marginBottom: 12, opacity: 0.5 }}
              />
              <Text style={{ color: theme ? '#6b7280' : '#9ca3af', textAlign: 'center', fontSize: 14, marginBottom: 8 }}>
                No groups available
              </Text>
              <Text style={{ color: theme ? '#9ca3af' : '#6b7280', textAlign: 'center', fontSize: 12 }}>
                Create a group first to organize your knowledge articles
              </Text>
            </View>
          ) : (
            groups.map((group) => {
              const isItemInGroup = itemToAddToGroup ? group.itemIds.includes(itemToAddToGroup) : false;
              return (
                <Pressable
                  key={group.id}
                  onPress={() => {
                    if (itemToAddToGroup) {
                      if (isItemInGroup) {
                        Alert.alert(
                          'Already in Group',
                          `This article is already in "${group.name}".`,
                          [{ text: 'OK' }]
                        );
                      } else {
                        handleAddItemToGroup(itemToAddToGroup, group.id);
                      }
                    }
                  }}
                  style={({ pressed }) => ({
                    padding: 16,
                    backgroundColor: isItemInGroup 
                      ? (theme ? '#eff6ff' : '#1e3a8a')
                      : (pressed 
                        ? (theme ? '#f1f5f9' : '#475569')
                        : (theme ? '#f8fafc' : '#374151')),
                    borderRadius: 12,
                    borderWidth: 2,
                    borderColor: isItemInGroup
                      ? (theme ? '#3b82f6' : '#60a5fa')
                      : (theme ? '#e5e7eb' : '#4b5563'),
                    opacity: pressed ? 0.8 : 1,
                    ...(Platform.OS === 'web' && {
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }),
                  })}
                >
                  <XStack alignItems="center" gap="$3">
                    <MaterialIcons
                      name={group.icon as any || "folder"}
                      size={24}
                      color={isItemInGroup 
                        ? (theme ? '#3b82f6' : '#60a5fa')
                        : (theme ? '#3b82f6' : '#60a5fa')}
                    />
                    <YStack flex={1}>
                      <XStack alignItems="center" gap="$2">
                        <Text style={{
                          color: theme ? '#1f2937' : '#f9fafb',
                          fontSize: 16,
                          fontWeight: '600',
                        }}>
                          {group.name}
                        </Text>
                        {isItemInGroup && (
                          <View style={{
                            backgroundColor: theme ? '#3b82f6' : '#60a5fa',
                            paddingHorizontal: 6,
                            paddingVertical: 2,
                            borderRadius: 4,
                          }}>
                            <Text style={{
                              color: 'white',
                              fontSize: 10,
                              fontWeight: '600',
                            }}>
                              ADDED
                            </Text>
                          </View>
                        )}
                      </XStack>
                      {group.category && (
                        <Text style={{
                          color: theme ? '#6b7280' : '#9ca3af',
                          fontSize: 12,
                          marginTop: 4,
                        }}>
                          Category: {group.category}
                        </Text>
                      )}
                      <Text style={{
                        color: theme ? '#9ca3af' : '#6b7280',
                        fontSize: 12,
                        marginTop: 4,
                      }}>
                        {group.itemIds.length} {group.itemIds.length === 1 ? 'item' : 'items'}
                      </Text>
                    </YStack>
                    {isItemInGroup ? (
                      <MaterialIcons
                        name="check-circle"
                        size={24}
                        color={theme ? '#3b82f6' : '#60a5fa'}
                      />
                    ) : (
                      <AntDesign name="right" size={16} color={theme ? '#6b7280' : '#9ca3af'} />
                    )}
                  </XStack>
                </Pressable>
              );
            })
          )}
        </YStack>
      </StyledModal>

      <StyledModal
        ref={knowldegeRef}
        title={`Add ${toTitleCase(knowledgeType)} Knowledge Article`}
        caption={`Create a new ${knowledgeType} knowledge article for your knowledge base`}
        isVisible={addKnowledge}
        onBackdropPress={() => setAddKnowledge(false)}
        style={{
          backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
          borderRadius: 24,
          elevation: 12,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 12 },
          shadowOpacity: 0.15,
          shadowRadius: 32,
          borderWidth: 1,
          borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
          ...(Platform.OS === 'web' && {
            backdropFilter: 'blur(24px)',
            boxShadow: '0 12px 48px rgba(0,0,0,0.12)',
          })
        }}
        headerStyle={{
          backgroundColor: theme ? '#f8fafc' : '#374151',
          borderBottomWidth: 1,
          borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
          paddingHorizontal: 24,
          paddingVertical: 20,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
        }}
        titleStyle={{
          fontSize: 20,
          fontWeight: '700',
          color: theme ? '#1e293b' : '#f1f5f9',
        }}
        captionStyle={{
          fontSize: 14,
          color: theme ? '#64748b' : '#94a3b8',
          fontWeight: '500',
        }}
        contentStyle={{
          padding: 24,
          backgroundColor: theme ? '#ffffff' : '#1e293b',
        }}
      >
        <View style={{
          backgroundColor: theme ? '#f8fafc' : '#374151',
          borderRadius: 16,
          padding: 20,
          marginBottom: 16,
          borderWidth: 1,
          borderColor: theme ? '#e5e7eb' : '#4b5563',
        }}>
          <GenerateSchemaForm
            schema={knowledgeType == 'internal' ? internalSchema : externalSchema}
            submitFunction={postSchema}
            formStyle={{
              backgroundColor: 'transparent',
            }}
            fieldStyle={{
              backgroundColor: theme ? '#ffffff' : '#1f2937',
              borderColor: theme ? '#e5e7eb' : '#4b5563',
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
            }}
            labelStyle={{
              color: theme ? '#374151' : '#f9fafb',
              fontWeight: '600',
              fontSize: 14,
              marginBottom: 8,
            }}
            buttonStyle={{
              backgroundColor: theme ? '#3b82f6' : '#2563eb',
              borderRadius: 12,
              paddingVertical: 16,
              paddingHorizontal: 24,
              elevation: 2,
              shadowColor: '#3b82f6',
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.2,
              shadowRadius: 8,
            }}
            buttonTextStyle={{
              color: '#ffffff',
              fontWeight: '600',
              fontSize: 16,
            }}
          />
        </View>
      </StyledModal>
      <YStack paddingTop={50} flex={1} gap={15} rowGap={5}> 
        {
          showChart && (
            <ErrorBoundary>
              <Animatable.View useNativeDriver style={{ flex: 2 }} animation="slideInDown">
                <Card flex={2} paddingVertical={3} margin={20} paddingBottom={30}>
                  <View style={{ flex: 2, minHeight: 220, top: "20%", borderRadius: 20, overflow: "hidden", minWidth: 300 }}>
                    {/* <DataChart /> */}
                    <LineGraph
                      animated={true}
                      gradientFillColors={['#58BFEA', '#ffffff30']}
                      enablePanGesture={true}
                      verticalPadding={40}
                      enableFadeInMask={true}
                      onGestureStart={() => {
                        console.log('Gesture Start')
                      }}
                      color='#58BFEA'
                      horizontalPadding={15}
                      SelectionDot={SelectionDot}
                      range={range}
                      enableIndicator
                      indicatorPulsating
                      points={points}
                    />
                  </View>
                </Card>
              </Animatable.View>
            </ErrorBoundary>
          )
        }
        <YStack flex={4}>
          {/* Groups FlatList */}
          <GroupsFlatList
            groups={groups}
            onGroupPress={handleGroupPress}
            onGroupDelete={handleGroupDelete}
            onGroupCreate={handleGroupCreate}
            onGroupRename={handleGroupRename}
            selectedGroupId={selectedGroupId}
            onItemAddToGroup={handleAddItemToGroup}
            onGroupDragOver={handleGroupDragOver}
            onGroupDragLeave={handleGroupDragLeave}
            onGroupDrop={handleGroupDrop}
            dragOverGroupId={dragOverGroupId}
          />

          {/* Category Labels */}
          {selectedGroupId && (
            <View style={{
              marginHorizontal: 20,
              marginBottom: 12,
              padding: 12,
              backgroundColor: theme ? '#f0f9ff' : '#0c4a6e',
              borderRadius: 12,
              borderWidth: 1,
              borderColor: theme ? '#bfdbfe' : '#1e3a8a',
            }}>
              <XStack alignItems="center" gap="$2">
                <MaterialIcons
                  name="label"
                  size={18}
                  color={theme ? '#3b82f6' : '#60a5fa'}
                />
                <Text style={{
                  color: theme ? '#1e40af' : '#93c5fd',
                  fontSize: 14,
                  fontWeight: '600',
                }}>
                  Showing items from: {groups.find(g => g.id === selectedGroupId)?.name || 'Group'}
                </Text>
                <Pressable
                  onPress={() => setSelectedGroupId(null)}
                  style={{ marginLeft: 'auto', padding: 4 }}
                >
                  <AntDesign name="close" size={16} color={theme ? '#3b82f6' : '#60a5fa'} />
                </Pressable>
              </XStack>
            </View>
          )}

          {/* Space for Commonly viewed knowledge articles */}
          <FlatList
            style={{ flex: 1 }}
            // horizontal={!isTablet}
            data={filteredKnowledgeData ?
              [...filteredKnowledgeData.internal, ...filteredKnowledgeData.external]
          .sort((a, b) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime())
              : []
            }
            // stickySectionHeadersEnabled
            numColumns={columns}
            showsVerticalScrollIndicator={false}
            key={uniqueId(columns.toString())}
            keyExtractor={(item) => item._id}
            ListHeaderComponent={HeaderComponent}
            columnWrapperStyle={columns > 1 ? { 
              justifyContent: 'space-between',
              marginBottom: 0, // Removed spacing
              paddingHorizontal: 0 // Removed padding
            } : undefined}
            ListEmptyComponent={(
              <View style={{ gap: 20, margin: 20, flex: 1 }}> {/* Reduced from gap: 30, margin: 30 */}
          <View style={[styles.centerEverything, { gap: 20, flex: 1, maxWidth: "100%", alignSelf: "center" }]}> {/* Reduced from gap: 30 */}
            <GlobalTutorial showArrows customHeight={450} customWidth={300} carouselData=
              {knowledgeBaseCarouselData} />
            <Button buttonText={"Add Knowledge"} onPress={() => _handleAddKnowledge('internal')} customStyle={{ minWidth: 200 }} />
          </View>
              </View>
            )}
            contentContainerStyle={{ paddingBottom: 100 }} // Reduced from paddingBottom: 150, paddingTop: 25
            renderItem={({ item, index }) => {
              // Calculate item width for grid layout
              const itemWidth = columns > 1 ? (100 / columns) : 100; // Removed spacing calculation
              
              return (
                <Pressable onPress={()=>{
                  // handleNavigation was undefined -- use navigation.navigate if available
                  navigation.navigate('Knowledge', { screen: 'EditKnowledge', params: { knowledgeArticleId: item._id } });
                }} style={{ 
                  width: columns > 1 ? `${itemWidth}%` : '100%',
                  flexBasis: columns > 1 ? `${itemWidth}%` : '100%',
                  flexGrow: 0,
                  flexShrink: 0,
                  paddingHorizontal: 0, // Removed all horizontal padding
                  marginBottom: 0, // Removed bottom margin
                }}>
                  <KnowledgeItem 
                    refetch={refetch} 
                    flipState={_handleFlip} 
                    item={item}
                    index={index}
                    maxWidth={undefined} // Remove fixed width constraint
                    onAddToGroup={handleOpenAddToGroupModal}
                    draggable={true}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  />
                </Pressable>
              );
            }}
          />

          {/* Pagination Controls for Web */}
          {finalKnowledgeData?.pagination?.combined && paginationState && (
            <Pagination
              pagination={{
                currentPage: finalKnowledgeData.pagination.combined.currentPage || 1,
                totalPages: finalKnowledgeData.pagination.combined.totalPages || 1,
                totalRecords: finalKnowledgeData.pagination.combined.totalRecords || 0,
                hasNextPage: finalKnowledgeData.pagination.combined.hasNextPage || false,
                hasPrevPage: finalKnowledgeData.pagination.combined.hasPrevPage || false,
                nextPage: finalKnowledgeData.pagination.combined.nextPage,
                prevPage: finalKnowledgeData.pagination.combined.prevPage
              }}
              compact
              pageSize={paginationState.pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[5, 10, 20, 50]}
              showSorting={true}
              sortBy={paginationState.sortBy}
              sortOrder={paginationState.sortOrder}
              onSortByChange={handleSortByChange}
              onSortOrderChange={handleSortOrderChange}
              sortByOptions={[
                { value: 'createdAt', label: 'Created Date' },
                { value: 'updatedAt', label: 'Updated Date' },
                { value: 'title', label: 'Title' },
                { value: 'name', label: 'Name' }
              ]}
            />
          )}
        </YStack>
        {newKnowledge && (
          <MotiView
            from={{ translateY: 700, opacity: 0 }}
            animate={{ translateY: 0, opacity: 1 }}
            exit={{ translateY: 700, opacity: 0 }}
            transition={{ type: 'timing', duration: 400 }}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 41, 59, 0.98)',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              padding: 0,
              zIndex: 100,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: -8 },
              shadowOpacity: 0.15,
              shadowRadius: 24,
              elevation: 12,
              minHeight: 600,
              borderTopWidth: 1,
              borderTopColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(24px)',
                boxShadow: '0 -8px 32px rgba(0,0,0,0.12)',
              })
            }}
          >
            {/* Header */}
            <XStack
              padding="$4"
              alignItems="center"
              justifyContent="space-between"
              style={{
                backgroundColor: theme ? '#f8fafc' : '#374151',
                borderBottomWidth: 1,
                borderBottomColor: theme ? '#e5e7eb' : '#4b5563',
                borderTopLeftRadius: 24,
                borderTopRightRadius: 24,
              }}
            >
              <XStack alignItems="center" gap="$3">
                <View style={{
                  backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
                  borderRadius: 12,
                  padding: 10,
                }}>
                  <MaterialIcons
                    name="library-add"
                    size={20}
                    color={theme ? "#0ea5e9" : "#38bdf8"}
                  />
                </View>
                <YStack>
                  <H2 style={{
                    color: theme ? '#1e293b' : '#f9fafb',
                    fontSize: 20,
                    fontWeight: '700',
                    margin: 0,
                  }}>
                    Add Knowledge Article
                  </H2>
                  <Text style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 14,
                  }}>
                    Create and publish new content
                  </Text>
                </YStack>
              </XStack>
              <Pressable 
                onPress={() => {
                  // Clean up image state when closing
                  if (featuredImageUrl) {
                    URL.revokeObjectURL(featuredImageUrl);
                  }
                  setFeaturedImage(null);
                  setFeaturedImageUrl(null);
                  setFeaturedImageBase64(null);
                  setImageProcessing(false);
                  setNewKnowledgeTitle('');
                  setNewKnowledge(false);
                }}
              >
                <AntDesign 
                  name="close" 
                  size={24} 
                  color={theme ? '#6b7280' : '#9ca3af'} 
                />
              </Pressable>
            </XStack>

            {/* Content */}
            <View style={{ flex: 1, padding: 16 }}>
              <YStack gap="$4">
                {/* Article Title */}
                <YStack gap="$2">
                  <SizableText 
                    color={theme ? '#1f2937' : '#f9fafb'} 
                    fontWeight="600"
                    size="$4"
                  >
                    Article Title *
                  </SizableText>
                  <TextInput
                    testID="add-knowledge-title-input"
                    placeholder="Enter knowledge article title..."
                    value={newKnowledgeTitle}
                    onChangeText={setNewKnowledgeTitle}
                    style={{ 
                      fontSize: 16, 
                      padding: 12,
                      borderWidth: 1,
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      color: theme ? '#1f2937' : '#f9fafb',
                      minHeight: 48,
                      fontWeight: '500',
                    }}
                    placeholderTextColor={theme ? '#9ca3af' : '#6b7280'}
                  />
                </YStack>

                {/* Featured Image */}
                <YStack gap="$2">
                  <SizableText 
                    style={{
                      color: theme ? '#374151' : '#f9fafb',
                      fontWeight: '600',
                      fontSize: 14,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Featured Image
                  </SizableText>
                  {featuredImage && featuredImageUrl ? (
                    <XStack 
                      alignItems="center" 
                      gap="$3"
                      style={{
                        padding: 16,
                        backgroundColor: theme ? '#f8fafc' : '#374151',
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: theme ? '#e5e7eb' : '#4b5563',
                      }}
                    >
                      <Image
                        source={{ uri: featuredImageUrl }}
                        style={{ 
                          width: 64, 
                          height: 64, 
                          borderRadius: 12, 
                          borderWidth: 1,
                          borderColor: theme ? '#e5e7eb' : '#4b5563',
                          opacity: imageProcessing ? 0.5 : 1
                        }}
                        resizeMode="cover"
                      />
                      <YStack flex={1} gap="$1">
                        <SizableText 
                          style={{
                            color: theme ? '#1f2937' : '#f9fafb',
                            fontWeight: '600',
                            fontSize: 16,
                          }}
                        >
                          Image Selected
                        </SizableText>
                        {imageProcessing && (
                          <SizableText style={{
                            fontSize: 12,
                            color: theme ? '#6b7280' : '#9ca3af'
                          }}>
                            Processing...
                          </SizableText>
                        )}
                      </YStack>
                      <Pressable
                        disabled={imageProcessing}
                        onPress={async () => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e: any) => {
                            const file = e.target.files[0];
                            if (file) {
                              setFeaturedImage(file);
                            }
                          };
                          input.click();
                        }}
                        style={{
                          backgroundColor: theme ? '#3b82f6' : '#2563eb',
                          borderRadius: 8,
                          paddingHorizontal: 16,
                          paddingVertical: 8,
                          opacity: imageProcessing ? 0.5 : 1,
                        }}
                      >
                        <Text style={{
                          color: '#ffffff',
                          fontWeight: '600',
                          fontSize: 14,
                        }}>
                          Replace
                        </Text>
                      </Pressable>
                    </XStack>
                  ) : (
                    <Pressable
                      onPress={async () => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e: any) => {
                          const file = e.target.files[0];
                          if (file) {
                            setFeaturedImage(file);
                          }
                        };
                        input.click();
                      }}
                      style={{
                        padding: 24,
                        backgroundColor: theme ? '#f8fafc' : '#374151',
                        borderWidth: 2,
                        borderColor: theme ? '#e5e7eb' : '#4b5563',
                        borderStyle: 'dashed',
                        borderRadius: 12,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AntDesign 
                        name="plus" 
                        size={32} 
                        color={theme ? '#6b7280' : '#9ca3af'} 
                      />
                      <SizableText 
                          style={{
                            color: theme ? '#6b7280' : '#9ca3af',
                            marginTop: 8,
                            fontWeight: '500',
                          }}
                      >
                        Click to select featured image
                      </SizableText>
                    </Pressable>
                  )}
                </YStack>

                {/* Publish Status */}
                <YStack gap="$2">
                  <SizableText 
                    style={{
                      color: theme ? '#374151' : '#f9fafb',
                      fontWeight: '600',
                      fontSize: 14,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Publish Status
                  </SizableText>
                  <Select 
                    defaultValue={"draft"} 
                    style={{ 
                      width: '100%',
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      padding: 16,
                      fontSize: 16,
                      fontWeight: '500',
                    }}
                  >
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="removed">Removed</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                  </Select>
                </YStack>

                {/* Editor Section */}
                <YStack gap="$2" flex={1}>
                  <SizableText 
                    style={{
                      color: theme ? '#374151' : '#f9fafb',
                      fontWeight: '600',
                      fontSize: 14,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Article Content *
                  </SizableText>
                  <View 
                    style={{ 
                      flex: 1, 
                      minHeight: 250, 
                      maxHeight: height * 0.6,
                      backgroundColor: theme ? '#f8fafc' : '#374151',
                      borderWidth: 1,
                      borderColor: theme ? '#e5e7eb' : '#4b5563',
                      borderRadius: 12,
                      overflow: 'hidden',
                    }}
                  >
                    <DomEditor 
                      hideBack 
                      ref={domEditorRef} 
                      saveFn={async (innerContent: string) => {
                        if (innerContent && newKnowledgeTitle) {
                          // Use the new SSE-enabled postInternalKnowledge function
                          await apis.postInternalKnowledge({
                            title: newKnowledgeTitle,
                            content: innerContent,
                            featuredImage: featuredImageBase64, // Send base64 instead of File object
                            type: 'internal',
                          }, (progressData) => {
                            // Handle progress updates
                            console.log('Knowledge creation progress:', progressData);
                            // You could show progress in the UI here if needed
                          }).then(() => {
                            // Clean up image state after saving
                            if (featuredImageUrl) {
                              URL.revokeObjectURL(featuredImageUrl);
                            }
                            setFeaturedImage(null);
                            setFeaturedImageUrl(null);
                            setFeaturedImageBase64(null);
                            setImageProcessing(false);
                            setNewKnowledge(false);
                            setNewKnowledgeTitle('');
                            refetch();
                          });
                        }
                      }} 
                    />
                  </View>
                </YStack>
              </YStack>
            </View>
          </MotiView>
        )}
      </YStack>

    </DefaultScreen>
  )
}
export default KnowledgeScreen;